import React from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Divider,
  Button
} from '@mui/material';
import {
  Gavel,
  WarningAmber,
  Copyright,
  ContactSupport,
  ArrowBack,
  Block,
  Update
} from '@mui/icons-material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { usePageSeo } from './usePageSeo';

const WhiteNoiseTerms = () => {
  const navigate = useNavigate();
  usePageSeo({
    title: 'Terms and Conditions | White Noise & Brown Noise',
    description: 'Terms and conditions for White Noise & Brown Noise, a free iOS sleep sounds app by Michael R. Ellis.',
    path: '/white-noise-terms'
  });

  return (
    <>
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/')}
          sx={{ mb: 3 }}
        >
          Back to Portfolio
        </Button>

        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Terms and Conditions
          </Typography>

          <Typography variant="body2" color="text.secondary" gutterBottom>
            Effective Date: September 27, 2026
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            App Name: White Noise &amp; Brown Noise
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Platform: iOS
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Developer: Michael R. Ellis
          </Typography>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <Gavel sx={{ mr: 1 }} />
              Agreement
            </Typography>
            <Typography variant="body1" paragraph>
              By downloading, installing, or using White Noise &amp; Brown Noise (the &quot;App&quot;), you agree to these Terms and Conditions. If you do not agree, do not use the App. The App is provided by Michael R. Ellis (&quot;we,&quot; &quot;us,&quot; or &quot;Developer&quot;).
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom>
              License to Use
            </Typography>
            <Typography variant="body1" paragraph>
              We grant you a limited, non-exclusive, non-transferable, revocable license to use the App on Apple devices you own or control, solely for personal, non-commercial purposes, and in accordance with the App Store Terms of Service and these Terms.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom>
              Cost
            </Typography>
            <Typography variant="body1" paragraph>
              The App is free. It contains no subscriptions, no in-app purchases, and no advertising. Every sound, scene, and play animation in the App is available to everyone at no charge.
            </Typography>
            <Typography variant="body1" paragraph>
              If paid features are introduced in a future version, these Terms will be updated with a new effective date before those features become available, and any pricing, billing, renewal, and refund terms will be described here at that time.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <Block sx={{ mr: 1 }} />
              Acceptable Use
            </Typography>
            <Typography variant="body1" paragraph>
              You agree not to:
            </Typography>
            <Typography component="ul" variant="body1" sx={{ pl: 3 }}>
              <li>Copy, modify, reverse engineer, decompile, or attempt to extract the source code of the App, except where applicable law expressly permits</li>
              <li>Redistribute, rent, lease, sublicense, or commercially exploit the App or its bundled audio assets without permission</li>
              <li>Use the App in any unlawful manner or in a way that interferes with its normal operation</li>
              <li>Remove or alter copyright, attribution, or proprietary notices</li>
              <li>Extract, record, or capture the App&apos;s audio in order to copy or redistribute it outside the App</li>
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <Copyright sx={{ mr: 1 }} />
              Intellectual Property &amp; Sound Content
            </Typography>
            <Typography variant="body1" paragraph>
              The App, including its design, software, trademarks, and original content, is owned by the Developer or its licensors and is protected by intellectual property laws. Sound and music assets included in the App may be licensed from third parties. Credits for contributors are shown in the App. You may use bundled sounds only as played through the App; you may not extract, remix outside the App for distribution, or republish those assets separately unless permitted by the applicable license.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom>
              Health &amp; Safety Disclaimer
            </Typography>
            <Typography variant="body1" paragraph>
              White Noise &amp; Brown Noise is for general wellness, relaxation, sleep, and focus use. It is not a medical device and is not intended to diagnose, treat, cure, or prevent any disease or condition. Listen at safe volume levels. Prolonged high-volume listening can damage hearing. If you have a medical concern related to sleep or hearing, consult a qualified professional.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <WarningAmber sx={{ mr: 1 }} />
              Disclaimer of Warranties
            </Typography>
            <Typography variant="body1" paragraph>
              The App is provided &quot;as is&quot; and &quot;as available,&quot; without warranties of any kind, whether express or implied, including merchantability, fitness for a particular purpose, and non-infringement. We do not warrant that the App will be uninterrupted, error-free, or free of defects, or that it will meet your specific requirements.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom>
              Limitation of Liability
            </Typography>
            <Typography variant="body1" paragraph>
              To the maximum extent permitted by law, the Developer shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of data, profits, or goodwill, arising from your use of or inability to use the App. Our total liability for any claim arising out of these Terms or the App shall not exceed the amount you paid for the App (if any) in the twelve (12) months preceding the claim, or ten US dollars (US $10), whichever is greater.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom>
              Apple App Store
            </Typography>
            <Typography variant="body1" paragraph>
              These Terms are between you and the Developer, not Apple Inc. Apple is not responsible for the App or its content. Apple has no obligation to provide maintenance or support. In the event of a failure of the App to conform to any applicable warranty, you may notify Apple, and Apple may refund the purchase price (if any) as required by App Store rules; to the maximum extent permitted by law, Apple has no other warranty obligation. Apple is a third-party beneficiary of these Terms and may enforce them against you.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom>
              Privacy
            </Typography>
            <Typography variant="body1" paragraph>
              Our handling of information is described in the{' '}
              <RouterLink to="/white-noise-privacy-policy" style={{ color: '#1976d2', textDecoration: 'none' }}>
                Privacy Policy
              </RouterLink>
              , which is incorporated by reference into these Terms.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <Update sx={{ mr: 1 }} />
              Changes &amp; Termination
            </Typography>
            <Typography variant="body1" paragraph>
              We may update these Terms by posting a revised version with a new effective date. Continued use after changes means you accept the updated Terms. We may suspend or terminate access to the App if you violate these Terms. You may stop using the App at any time by uninstalling it.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom>
              Governing Law
            </Typography>
            <Typography variant="body1" paragraph>
              These Terms are governed by the laws of the State of New York, USA, without regard to conflict-of-law principles, except where mandatory consumer protection laws in your jurisdiction require otherwise.
            </Typography>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="h5" component="h2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <ContactSupport sx={{ mr: 1 }} />
              Contact
            </Typography>
            <Typography variant="body1" paragraph>
              Questions about these Terms:{' '}
              <a href="mailto:Contact@MichaelRobertEllis.com" style={{ color: '#1976d2', textDecoration: 'none' }}>
                Contact@MichaelRobertEllis.com
              </a>
            </Typography>
          </Box>
        </Paper>
      </Container>
    </>
  );
};

export default WhiteNoiseTerms;
